import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MailCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function VerificationSent() {
  useSEO({ title: "Vérifiez votre email", description: "Un email de confirmation vient d'être envoyé. Cliquez sur le lien pour activer votre compte DemkaBiz." });
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [sending, setSending] = useState(false);

  const resend = async () => {
    if (!email) return;
    setSending(true);
    try {
      await api.post("/auth/resend-verification-public", { email });
      toast.success("Un nouveau lien de vérification a été envoyé.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="verification-sent-page">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary mb-6">
        <MailCheck className="w-10 h-10 text-primary" />
      </div>
      <h1 className="font-display font-bold text-3xl tracking-tight">Vérifiez votre email</h1>
      <p className="text-muted-foreground mt-3">
        Nous avons envoyé un lien de confirmation à
        {email ? <> <strong className="text-foreground">{email}</strong></> : <> votre adresse email</>}.
      </p>
      <div className="rounded-2xl bg-secondary/40 border border-primary/10 p-5 mt-6 text-left">
        <ol className="text-sm space-y-2 list-decimal list-inside text-foreground/80">
          <li>Ouvrez votre boîte de réception.</li>
          <li>Cliquez sur le bouton <strong>« Confirmer mon email »</strong> dans le message reçu.</li>
          <li>Vous serez redirigé(e) vers DemkaBiz pour activer votre compte et vous connecter.</li>
        </ol>
      </div>

      <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 mt-4 text-left" data-testid="verification-sent-spam-warning">
        <p className="text-sm text-amber-900 font-semibold flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
          Important : vérifiez vos courriers indésirables (spams)
        </p>
        <p className="text-sm text-amber-800 mt-2 leading-relaxed">
          Si vous ne trouvez pas notre email dans votre boîte de réception d'ici quelques minutes, il est probablement dans votre dossier <strong>Spams</strong> ou <strong>Courriers indésirables</strong>. Ajoutez <code className="bg-amber-100 px-1.5 py-0.5 rounded">contact@demka.ca</code> à vos contacts pour éviter ce souci.
        </p>
      </div>

      <div className="mt-8 space-y-3 text-sm text-muted-foreground">
        <p>Vous n'avez pas reçu d'email ?</p>
        {email && (
          <button
            onClick={resend}
            disabled={sending}
            className="btn-outline w-full justify-center"
            data-testid="verification-sent-resend"
          >
            <RefreshCw className={`w-4 h-4 ${sending ? "animate-spin" : ""}`} />
            {sending ? "Envoi en cours…" : "Renvoyer l'email de vérification"}
          </button>
        )}
        <p className="text-xs">Un souci ? Écrivez-nous à <a href="mailto:contact@demka.ca" className="text-primary hover:underline">contact@demka.ca</a>.</p>
      </div>
    </div>
  );
}
